'use client'

import Link from '@components/primitives/Link'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import type { ConjuredFaqCitation } from '@modules/trials/helpers/getConjuredFaqCitations'
import type { ClinicalTrialConjuredFaq } from '@prisma/client'
import type { Pick } from '@prisma/client/runtime/library'
import { useMemo, useState } from 'react'
import { jsonLdScriptProps } from 'react-schemaorg'
import type { FAQPage } from 'schema-dts'
import StructuredData from '../StructuredData'
import { buildConjuredFaqEntity } from './helpers/buildConjuredFaqEntity'
import lineBreakParagraph from './helpers/lineBreakParagraph'

export type ConjuredRagFaqWidget = {
  citations: any
  content: string
  key: string
  simplifiedQuestion: string
  title: string
}

export default function TrialRagFaqWidget({
  conjuredFaqCitations,
  faqsWithRag,
}: {
  conjuredFaqCitations: ConjuredFaqCitation[]
  faqsWithRag: Pick<
    ClinicalTrialConjuredFaq,
    | 'mechanismOfAction'
    | 'sideEffects'
    | 'priorApprovals'
    | 'promisingAlternatives'
  >
}) {
  const [activeElement, setActiveElement] =
    useState<string>('mechanismOfAction')

  const faqName = 'Treatment Intervention'

  const tabs = useMemo(
    () => generateWidgetTabs(faqsWithRag, conjuredFaqCitations),
    [faqsWithRag, conjuredFaqCitations],
  )

  const faqEntities = useMemo(() => {
    return Object.values(tabs).map((faq) => buildConjuredFaqEntity(faq))
  }, [tabs])

  // `faqEntities` contains escaped HTML entities, as per
  // https://www.w3.org/TR/json-ld11/#restrictions-for-contents-of-json-ld-script-elements
  const scriptTagPropsForJsonLd = jsonLdScriptProps<FAQPage>({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqEntities,
    'name': faqName,
  })

  return (
    <>
      <StructuredData
        id={`${faqName.toLowerCase().replace(/\s+/gs, '-')}-faqs`}
        scriptTagProps={scriptTagPropsForJsonLd}
      />
      <div className='grid w-full grid-cols-1 rounded-2xl border border-neutral200 md:grid-cols-4 md:gap-4'>
        <div className='inline w-full justify-center space-y-1 rounded-t-2xl border-b border-neutral200 bg-neutral50 py-6 md:rounded-l-2xl md:rounded-tr-none md:border-b-0 md:border-r'>
          {tabs.map(({ key, title }, index) => (
            <div
              className={classMerge(
                `mx-4 cursor-pointer rounded-full px-2 py-1 transition-all duration-200 hover:bg-neutral200`,
                activeElement === key ? 'bg-neutral200' : '',
              )}
              key={index}
              onClick={() => setActiveElement(key)}
            >
              <Text
                className='px-2'
                styleName={activeElement === key ? 'p-small-strong' : 'p-small'}
              >
                {title}
              </Text>
            </div>
          ))}
        </div>
        <div className='col-span-1 bg-white p-4 py-6 md:col-span-3 md:py-7 md:pr-8'>
          {tabs.map(({ content, key }) => (
            <FaqElement
              activeElement={activeElement}
              citations={conjuredFaqCitations}
              elementName={key}
              key={key}
              text={content}
            />
          ))}
        </div>
      </div>
    </>
  )
}

const FaqElement = ({
  activeElement,
  citations,
  elementName,
  text,
}: {
  activeElement?: string
  citations: ConjuredFaqCitation[]
  elementName?: string
  text: string
}) => {
  return (
    <div>
      <Text
        className={`whitespace-pre-wrap text-neutral600 ${activeElement === elementName ? '' : 'hidden'}`}
        styleName='p-small'
        value={text}
      />
      {citations && (
        <>
          {citations.filter((citation) => citation.key === elementName).length >
            0 && (
            <hr
              className={`my-4 border-neutral200 ${activeElement === elementName ? '' : 'hidden'}`}
            />
          )}
          {citations.map(
            (citation, index) =>
              citation.key === elementName && (
                <Link href={citation.url} key={index}>
                  <Text
                    className={`truncate text-neutral600 underline decoration-neutral400 hover:text-neutral900 ${activeElement === elementName ? '' : 'hidden'}`}
                    styleName='p-xtiny'
                    value={`${citation.title}`}
                  />
                </Link>
              ),
          )}
        </>
      )}
    </div>
  )
}

const generateWidgetTabs = (
  faqsWithRag: Pick<
    ClinicalTrialConjuredFaq,
    | 'mechanismOfAction'
    | 'sideEffects'
    | 'priorApprovals'
    | 'promisingAlternatives'
  >,
  conjuredFaqCitations: ConjuredFaqCitation[],
) => {
  const tabs: ConjuredRagFaqWidget[] = []

  if (!faqsWithRag) {
    return tabs
  }

  if (faqsWithRag.mechanismOfAction) {
    tabs.push({
      citations: conjuredFaqCitations?.filter(
        (citation) => citation.key === 'mechanismOfAction',
      ),
      content: lineBreakParagraph(faqsWithRag.mechanismOfAction),
      key: 'mechanismOfAction',
      simplifiedQuestion: 'What is the mechanism of action of this treatment?',
      title: 'Mechanism Of Action',
    })
  }

  if (faqsWithRag.sideEffects) {
    tabs.push({
      citations: conjuredFaqCitations?.filter(
        (citation) => citation.key === 'sideEffects',
      ),
      content: lineBreakParagraph(faqsWithRag.sideEffects),
      key: 'sideEffects',
      simplifiedQuestion:
        'What side effects are associated with this type of intervention?',
      title: 'Side Effect Profile',
    })
  }

  if (faqsWithRag.priorApprovals) {
    tabs.push({
      citations: conjuredFaqCitations?.filter(
        (citation) => citation.key === 'priorApprovals',
      ),
      content: lineBreakParagraph(faqsWithRag.priorApprovals),
      key: 'priorApprovals',
      simplifiedQuestion: 'What prior FDA approvals exist?',
      title: 'Prior Approvals',
    })
  }

  if (faqsWithRag.promisingAlternatives) {
    tabs.push({
      citations: conjuredFaqCitations?.filter(
        (citation) => citation.key === 'promisingAlternatives',
      ),
      content: lineBreakParagraph(faqsWithRag.promisingAlternatives),
      key: 'promisingAlternatives',
      simplifiedQuestion: 'What other types of research exist?',
      title: 'Other Research',
    })
  }

  return tabs
}
